import { useState } from 'react';

import ChecklistIcon from '@mui/icons-material/Checklist';
import ShareIcon from '@mui/icons-material/Share';
import useUser, { isUserSubscribed } from '../authentication/context/useUser';

import Tooltip from '@mui/material/Tooltip';
import ShareProjectModal from './ShareProjectModal';

import { Project, ProjectUser } from '@gameonly/core';
import { useTranslation } from 'react-i18next';
import { ExportMenu } from '../layout/Header/ExportMenu';

import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { isPage } from '../layout/Header/ProjectBreadCrumb';
import { MenuItemTeasing } from '../subscription/SubscriptionTeasing';
import { SubscriptionButton } from '../ui/Button';
import DuplicateProject, { DuplicateProjectModal } from './DuplicateProject';
import SubscriptionModal from '../subscription/SubscriptionModal';

const ProjectActions = ({ project }: { project: Project }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openShare, setOpenShare] = useState<boolean>(false);
  const [openDuplicate, setOpenDuplicate] = useState<boolean>(false);
  const [subscriptionModal, setSubscriptionModal] = useState<{
    open: boolean;
    title: string;
    text: string;
    link: string;
  }>({ open: false, title: '', text: '', link: '' });
  const open = Boolean(anchorEl);
  const location = useLocation();
  const { isProject } = isPage(location);
  const { t } = useTranslation(['project']);

  if (!isProject) return null;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenShare = () => {
    setOpenShare(true);
    handleClose();
  };

  const handleOpenDuplicate = () => {
    setOpenDuplicate(true);
    handleClose();
  };

  const handleOpenSubscription = (title: string, text: string, link: string) => {
    setSubscriptionModal({ open: true, title, text, link });
    handleClose();
  };

  return (
    <>
      <Tooltip title={t('project:options-button.tooltip')}>
        <SubscriptionButton onClick={handleClick}>
          {t('project:options-button.label')}
        </SubscriptionButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <DuplicateProject project={project} onOpenDuplicate={handleOpenDuplicate} onClose={handleClose} onOpenSubscription={handleOpenSubscription} />
        <ShareProject 
          project={project} 
          onOpenShare={handleOpenShare}
          onOpenSubscription={handleOpenSubscription}
        />
        <ExportMenu project={project} onOpenSubscription={handleOpenSubscription} />
        {project.model?.type === 'site' && (
          <DownloadExcelMenuItem
            filename="Listing des données - Locaux.xlsx"
            type="site"
            onOpenSubscription={handleOpenSubscription}
          />
        )}
        {project.model?.type === 'game' && (
          <DownloadExcelMenuItem
            filename="Listing des données - Jeux.xlsx"
            type="game"
            onOpenSubscription={handleOpenSubscription}
          />
        )}
      </Menu>
      {openShare && (
        <ShareProjectModal
          open={openShare}
          onClose={() => setOpenShare(false)}
          project={project}
        />
      )}
      <DuplicateProjectModalWrapper 
        project={project} 
        open={openDuplicate} 
        onClose={() => setOpenDuplicate(false)} 
      />
      {subscriptionModal.open && (
        <SubscriptionModal
          open={subscriptionModal.open}
          onClose={() => setSubscriptionModal({ ...subscriptionModal, open: false })}
          title={subscriptionModal.title}
          text={subscriptionModal.text}
          link={subscriptionModal.link}
        />
      )}
    </>
  );
};

const ShareProject = ({
  project,
  onOpenShare,
  onOpenSubscription,
}: {
  project: Project;
  onOpenShare: () => void;
  onOpenSubscription: (title: string, text: string, link: string) => void;
}) => {
  const { user } = useUser();
  const { t } = useTranslation(['project']);

  const isShareProject = project?.users?.some(
    (u: ProjectUser) => u.id === user?._id && u.role === 'owner',
  );

  if (isShareProject && !isUserSubscribed(user)) {
    return (
      <MenuItemTeasing
        teasingButton={t('common:subscription.shareProject.teasingButton')}
        text={t('common:subscription.shareProject.modalText')}
        title={t('common:subscription.shareProject.modalTitle')}
        link={t('common:subscription.shareProject.knowMoreLink')}
        onOpen={() => onOpenSubscription(
          t('common:subscription.shareProject.modalTitle'),
          t('common:subscription.shareProject.modalText'),
          t('common:subscription.shareProject.knowMoreLink')
        )}
      />
    );
  }

  return (
    <>
      {isShareProject && (
        <MenuItem onClick={onOpenShare}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('project:share-button.label')}</ListItemText>
        </MenuItem>
      )}
    </>
  );
};

const DownloadExcelMenuItem = ({
  filename,
  type,
  onOpenSubscription,
}: {
  filename: string;
  type: string;
  onOpenSubscription: (title: string, text: string, link: string) => void;
}) => {
  const { user } = useUser();
  const { t } = useTranslation();

  if (!isUserSubscribed(user)) {
    return (
      <MenuItemTeasing
        teasingButton={t(
          `common:subscription.downloadDataList.${type}.teasingButton`,
        )}
        text={t(`common:subscription.downloadDataList.${type}.modalText`)}
        title={t(`common:subscription.downloadDataList.${type}.modalTitle`)}
        link={t(`common:subscription.downloadDataList.${type}.knowMoreLink`)}
        onOpen={() => onOpenSubscription(
          t(`common:subscription.downloadDataList.${type}.modalTitle`),
          t(`common:subscription.downloadDataList.${type}.modalText`),
          t(`common:subscription.downloadDataList.${type}.knowMoreLink`)
        )}
      />
    );
  }

  const handleDownload = () => {
    window.location.href = `/${filename}`;
  };

  return (
    <MenuItem onClick={handleDownload}>
      <ListItemIcon>
        <ChecklistIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>
        {t(`common:subscription.downloadDataList.${type}.teasingButton`)}
      </ListItemText>
    </MenuItem>
  );
};

const DuplicateProjectModalWrapper = ({ 
  project, 
  open, 
  onClose 
}: { 
  project: Project;
  open: boolean;
  onClose: () => void;
}) => {
  if (!open) return null;
  
  return (
    <DuplicateProjectModal 
      project={project} 
      onClose={onClose} 
    />
  );
};

export default ProjectActions;
